/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import { Close } from '@ahamove/icons';
import {
  Button,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { openContextModal } from '@mantine/modals';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalName } from '@/components/modals';
import { useGetPackageSizeDefinitions } from '@/features/booking/api/master-data/use-get-package-size-definitions';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export interface SizeButtonProps {
  onChange: (key: string, value: any) => void;
}

const SizeSection = ({ onChange }: SizeButtonProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { bikePackageSize } = useBookingStore((state) => state);

  const { data: sizeDefinitionList = [], isLoading } =
    useGetPackageSizeDefinitions();

  const [sizeState, setSizeState] = useState<string>(bikePackageSize);

  const handleShowPackageSizeDefinition = () => {
    openContextModal({
      modal: ModalName.PackageSizeDefinition,
      innerProps: {},
      title: 'Kích cỡ hàng hóa',
      h: 'fit-content',
      closeOnClickOutside: false,
      withCloseButton: true,
      closeButtonProps: {
        pos: isMobile ? 'absolute' : 'unset',
        mr: isMobile ? 0 : rem(-10),
        left: rem(12),
        top: isMobile ? 'auto' : rem(8),
        radius: 'xl',
        icon: <Close width={22} height={22} />,
      },
      fullScreen: isMobile,
      centered: true,
      maw: rem(440),
      w: '100%',
      styles: {
        root: {
          zIndex: 1,
        },
        title: {
          flex: 1,
          marginLeft: isMobile ? 0 : rem(14),
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 700,
        },
        header: {
          minHeight: isMobile ? rem(45) : rem(56),
          padding: isMobile ? '0px 16px' : '0px 24px',
          borderBottom: `1px solid var(--mantine-color-neutral-0)`,
        },
        body: {
          padding: 0,
        },
      },
    });
  };

  const debouncedSetSize = useCallback(
    debounce(() => {
      onChange('size', sizeState);
    }, 300),
    [sizeState]
  );

  useEffect(() => {
    setSizeState(bikePackageSize);
  }, [bikePackageSize]);

  useEffect(() => {
    debouncedSetSize();
    return () => {
      debouncedSetSize.cancel();
    };
  }, [debouncedSetSize]);

  const descriptionSize = useMemo(() => {
    return sizeDefinitionList.find((item) => item.size === sizeState)
      ?.description;
  }, [sizeState, sizeDefinitionList]);

  return (
    <>
      <Stack gap={8} px="md" py={rem(6)}>
        <Group justify="space-between">
          <Group>
            <Title order={isMobile ? 6 : 4} size={isMobile ? 'sm' : 'md'}>
              Kích cỡ{' '}
              <Text component="span" c="red">
                *
              </Text>
            </Title>
          </Group>
          <Button
            onClick={handleShowPackageSizeDefinition}
            variant="subtle"
            color="primary"
            justify="flex-start"
            radius={rem(4)}
            size="compact-md"
            h="auto"
            lh={1.5}
            mr={-8}
            styles={{
              inner: {
                fontSize: isMobile ? rem(14) : rem(16),
                fontWeight: 700,
              },
            }}
          >
            Xem hàng hóa
          </Button>
        </Group>
        {isLoading ? (
          <Skeleton radius={4} w={'40%'} h={25} />
        ) : (
          <Text h={25} size={isMobile ? 'sm' : 'md'}>
            {descriptionSize}
          </Text>
        )}

        <Group gap={8}>
          {isLoading
            ? Array(6)
                .fill(0)
                .map((_, index) => (
                  <Skeleton radius={40} key={index} w={40} h={40} />
                ))
            : sizeDefinitionList.map(({ size }) => (
                <Button
                  id={size}
                  key={size}
                  variant={sizeState === size ? 'filled' : 'outline'}
                  c={sizeState === size ? 'primary' : 'neutral.7'}
                  onClick={() => setSizeState(size)}
                  style={{
                    width: rem(40),
                    height: rem(40),
                    borderRadius: '50%',
                    borderColor:
                      sizeState === size
                        ? 'var(--mantine-color-primary-6)'
                        : 'var(--mantine-color-neutral-4)',
                    backgroundColor:
                      sizeState === size
                        ? 'var(--mantine-color-primary-2)'
                        : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    fw={sizeState === size ? 600 : 500}
                    size={isMobile ? 'sm' : 'md'}
                  >
                    {size}
                  </Text>
                </Button>
              ))}
        </Group>
      </Stack>
    </>
  );
};

export default SizeSection;
